import { lazy, Suspense, useEffect, useState, type ComponentType, type LazyExoticComponent, type ReactNode } from 'react'
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { api, ApiError } from './lib/api'

/*
 * Routes are code-split: every top-level page is its own lazy chunk. The
 * initial download stays small, and — crucially — a participant never pulls
 * the admin code. TipTap (ProseMirror), the heaviest dependency in the app,
 * is reachable only from the workshop editor, so it rides in that chunk and
 * loads only when an admin actually opens it.
 */

const CHUNK_RELOAD_KEY = 'wc-chunk-reload'

/**
 * React.lazy wrapper that survives a deploy. A chunk fetch failing almost
 * always means a new build replaced the hashed file mid-session — reload once
 * to pick up the fresh index.html + chunk manifest. The sessionStorage guard
 * prevents a reload loop when the cause is something else, and is cleared on
 * any successful load so a later deploy in the same session still recovers.
 */
function lazyRoute(
  factory: () => Promise<{ default: ComponentType }>,
): LazyExoticComponent<ComponentType> {
  return lazy(async () => {
    try {
      const mod = await factory()
      sessionStorage.removeItem(CHUNK_RELOAD_KEY)
      return mod
    } catch (err) {
      if (!sessionStorage.getItem(CHUNK_RELOAD_KEY)) {
        sessionStorage.setItem(CHUNK_RELOAD_KEY, '1')
        window.location.reload()
        // Stall render until the reload takes over — no flash of an error screen.
        return new Promise<{ default: ComponentType }>(() => {})
      }
      throw err
    }
  })
}

const Home = lazyRoute(() => import('./pages/Home').then((m) => ({ default: m.Home })))
const Legal = lazyRoute(() => import('./pages/Legal').then((m) => ({ default: m.Legal })))
const Lobby = lazyRoute(() => import('./pages/Lobby').then((m) => ({ default: m.Lobby })))
const LicenseRequest = lazyRoute(() =>
  import('./pages/LicenseRequest').then((m) => ({ default: m.LicenseRequest })),
)
const AdminLogin = lazyRoute(() => import('./pages/AdminLogin').then((m) => ({ default: m.AdminLogin })))
const AdminDashboard = lazyRoute(() =>
  import('./admin/Dashboard').then((m) => ({ default: m.AdminDashboard })),
)
const PlatformSettings = lazyRoute(() =>
  import('./admin/PlatformSettings').then((m) => ({ default: m.PlatformSettings })),
)
const Templates = lazyRoute(() => import('./admin/Templates').then((m) => ({ default: m.Templates })))
const AdminCatalog = lazyRoute(() =>
  import('./pages/AdminCatalog').then((m) => ({ default: m.AdminCatalog })),
)
const AdminManagement = lazyRoute(() =>
  import('./admin/AdminManagement').then((m) => ({ default: m.AdminManagement })),
)
const ArchivedWorkshops = lazyRoute(() =>
  import('./admin/ArchivedWorkshops').then((m) => ({ default: m.ArchivedWorkshops })),
)
const AdminProfile = lazyRoute(() =>
  import('./admin/AdminProfile').then((m) => ({ default: m.AdminProfile })),
)
const AdminSignup = lazyRoute(() =>
  import('./pages/AdminSignup').then((m) => ({ default: m.AdminSignup })),
)
const AdminAcceptInvite = lazyRoute(() =>
  import('./pages/AdminAcceptInvite').then((m) => ({ default: m.AdminAcceptInvite })),
)
const AdminAuditPage = lazyRoute(() =>
  import('./admin/AdminAuditPage').then((m) => ({ default: m.AdminAuditPage })),
)
const AdminProjectorLeaderboard = lazyRoute(() =>
  import('./admin/AdminProjectorLeaderboard').then((m) => ({ default: m.AdminProjectorLeaderboard })),
)
const WorkshopEditor = lazyRoute(() =>
  import('./admin/WorkshopEditor').then((m) => ({ default: m.WorkshopEditor })),
)
const WorkshopHome = lazyRoute(() =>
  import('./user/WorkshopHome').then((m) => ({ default: m.WorkshopHome })),
)
const WorkshopRegister = lazyRoute(() =>
  import('./pages/WorkshopAuth').then((m) => ({ default: m.WorkshopRegister })),
)
const WorkshopLogin = lazyRoute(() =>
  import('./pages/WorkshopAuth').then((m) => ({ default: m.WorkshopLogin })),
)
const WorkshopForgotPassword = lazyRoute(() =>
  import('./pages/WorkshopAuth').then((m) => ({ default: m.WorkshopForgotPassword })),
)
const WorkshopResetPassword = lazyRoute(() =>
  import('./pages/WorkshopAuth').then((m) => ({ default: m.WorkshopResetPassword })),
)
const ProjectorLeaderboard = lazyRoute(() =>
  import('./user/ProjectorLeaderboard').then((m) => ({ default: m.ProjectorLeaderboard })),
)

/** Minimal fill shown while a route chunk downloads — usually a sub-100ms flash. */
function RouteFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center text-sm text-slate-500">
      Loading…
    </div>
  )
}

export function Router() {
  return (
    <BrowserRouter>
      <Suspense fallback={<RouteFallback />}>
        <GateGuard>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/legal" element={<Legal />} />
            <Route path="/lobby" element={<Lobby />} />
            <Route path="/license/request" element={<LicenseRequest />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/signup" element={<AdminSignup />} />
            <Route path="/admin/invite/:token" element={<AdminAcceptInvite />} />
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/admin/settings" element={<PlatformSettings />} />
            <Route path="/admin/manage-admins" element={<AdminManagement />} />
            <Route path="/admin/archived" element={<ArchivedWorkshops />} />
            <Route path="/admin/me" element={<AdminProfile />} />
            <Route path="/admin/templates" element={<Templates />} />
            {/* Old clone-gallery path redirects to the unified templates page. */}
            <Route path="/admin/clone-gallery" element={<Navigate to="/admin/templates" replace />} />
            <Route path="/admin/audit" element={<AdminAuditPage />} />
            <Route path="/admin/workshops/:id/projector" element={<AdminProjectorLeaderboard />} />
            <Route path="/admin/workshops/:id" element={<WorkshopEditor />} />
            <Route path="/admin/*" element={<Navigate to="/admin" replace />} />
            <Route path="/by/:adminSlug" element={<AdminCatalog />} />
            <Route path="/:slug" element={<Navigate to="" replace />} />
            <Route path="/:slug/" element={<WorkshopHome />} />
            <Route path="/:slug/register" element={<WorkshopRegister />} />
            <Route path="/:slug/login" element={<WorkshopLogin />} />
            <Route path="/:slug/forgot-password" element={<WorkshopForgotPassword />} />
            <Route path="/:slug/reset/:token" element={<WorkshopResetPassword />} />
            <Route path="/:slug/leaderboard" element={<ProjectorLeaderboard />} />
          </Routes>
        </GateGuard>
      </Suspense>
    </BrowserRouter>
  )
}

/**
 * Client-side enforcement of the Public Facing Lobby gate. Fetches
 * `/api/gate/status` once on mount; if the gate is on and the visitor
 * doesn't already hold a verified cookie, redirects to `/lobby?return=…`.
 *
 * The server still enforces the gate on `/api/*` (the actual boundary) —
 * this is purely a UX layer so visitors don't watch failed fetches in
 * devtools and SPA chunks aren't loaded just to be redirected away.
 *
 * Routes that must always render (so the visitor has a way through or back):
 *   - `/lobby`        — the gate page itself
 *   - `/admin/*`      — admin login + dashboard are never gated
 *   - `/legal`        — public legal page; reachable for compliance reasons
 *   - `/license`      — license request form; public by design (the server
 *                       bypasses /api/license/ too) and linked from the
 *                       unlicensed banner
 *   - `/assets/*`     — static SPA bundle (only the router handles these
 *                       anyway when SPA shell serves them through fastify)
 */
const ALWAYS_OPEN_PREFIXES = ['/lobby', '/admin', '/legal', '/license']
function isAlwaysOpenPath(pathname: string): boolean {
  return ALWAYS_OPEN_PREFIXES.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`),
  )
}

function GateGuard({ children }: { children: ReactNode }) {
  const location = useLocation()
  // `checked` distinguishes "haven't asked the server yet" (don't render
  // children — we might be about to redirect) from "asked, gate's off or
  // we're verified" (render). Avoids a one-frame flash of the gated route
  // before the redirect fires.
  const [checked, setChecked] = useState(false)
  const [status, setStatus] = useState<{ enabled: boolean; verified: boolean } | null>(null)

  useEffect(() => {
    let cancelled = false
    api
      .gateStatus()
      .then((s) => {
        if (cancelled) return
        setStatus({ enabled: s.enabled, verified: s.verified })
        setChecked(true)
      })
      .catch((err) => {
        if (cancelled) return
        // If the status endpoint itself 401s with `lobby_locked` (a deploy
        // glitch where the gate is on but the route ended up gated), treat
        // it as "gate on, not verified" so we still redirect rather than
        // hanging on a blank screen.
        if (err instanceof ApiError && err.code === 'lobby_locked') {
          setStatus({ enabled: true, verified: false })
        } else {
          setStatus({ enabled: false, verified: true })
        }
        setChecked(true)
      })
    return () => {
      cancelled = true
    }
  }, [])

  if (!checked) {
    return (
      <div className="flex min-h-screen items-center justify-center text-sm text-slate-500">
        Loading…
      </div>
    )
  }

  if (status && status.enabled && !status.verified && !isAlwaysOpenPath(location.pathname)) {
    // Pass the original target as `?return=…` so a successful verify lands
    // the visitor where they actually wanted to be. `replace` so the
    // browser's Back button doesn't ping-pong them through the lobby.
    const ret = encodeURIComponent(location.pathname + location.search)
    return <Navigate to={`/lobby?return=${ret}`} replace />
  }

  return <>{children}</>
}
